import { CategoryData, ComparisonData } from 'src/app/shared/models/interfaces';

// All emissions categories ordered from darkest to lightest for proper stacking
// baseline-constant is first to appear at the bottom of stacked bars
export const ALL_CATEGORIES: CategoryData[] = [
  {
    key: 'baseline-constant',
    label: 'Baseline',
    color: 'var(--baseline-constant)',
    emissions: 0,
  },
  {
    key: 'baseline-digital',
    label: 'Digitalization',
    color: 'var(--baseline-digital)',
    emissions: 0,
  },
  {
    key: 'baseline-mobility',
    label: 'Mobility',
    color: 'var(--baseline-mobility)',
    emissions: 0,
  },
  {
    key: 'baseline-general-consumption',
    label: 'General Consumption',
    color: 'var(--baseline-general-consumption)',
    emissions: 0,
  },
  {
    key: 'baseline-housing',
    label: 'Housing',
    color: 'var(--baseline-housing)',
    emissions: 0,
  },
  {
    key: 'daily-activity',
    label: 'Digital Activities',
    color: 'var(--daily-activity)',
    emissions: 0,
  },
  {
    key: 'daily-mobility',
    label: 'Mobility',
    color: 'var(--daily-mobility)',
    emissions: 0,
  },
  {
    key: 'daily-food',
    label: 'Food',
    color: 'var(--daily-food)',
    emissions: 0,
  },
];

export const BASELINE_CATEGORIES = ALL_CATEGORIES.slice(0, 5);
export const DAILY_CATEGORIES = ALL_CATEGORIES.slice(5, 8);

export function getCategoryColor(categoryKey: string): string {
  return ALL_CATEGORIES.find((c) => c.key === categoryKey)?.color || '#000';
}

export function generateYTicks(min: number, max: number): number[] {
  const range = max - min;
  const targetTickCount = 8;
  const step = Math.max(1, Math.ceil(range / targetTickCount));
  const ticks: number[] = [];

  const startTick = Math.floor(min / step) * step;

  for (let i = startTick; i <= max; i += step) {
    ticks.push(i);
  }

  return ticks;
}

// functions used by the emissions and dashboard chart component to calculate daily and baseline emissions
export function calculateBaselineEmissions(
  reportData: any
): Record<string, number> {
  const emissions: Record<string, number> = {
    'baseline-digital': 0,
    'baseline-mobility': 0,
    'baseline-general-consumption': 0,
    'baseline-housing': 0,
    'baseline-constant': 0,
  };

  // map baseline questionnaire responses to categories (yearly values divided by 365)
  reportData.baseline?.forEach((entry: any) => {
    const categoryText =
      entry.baselineQuestions?.category?.text?.text_en ||
      entry.baselineQuestions?.category?.text?.text_de;
    const dailyEmission = (entry.causedEmissions || 0) / 365;

    if (categoryText?.toLowerCase().includes('housing')) {
      emissions['baseline-housing'] += dailyEmission;
    } else if (categoryText?.toLowerCase().includes('consumption')) {
      emissions['baseline-general-consumption'] += dailyEmission;
    } else if (categoryText?.toLowerCase().includes('mobility')) {
      emissions['baseline-mobility'] += dailyEmission;
    } else {
      emissions['baseline-digital'] += dailyEmission;
    }
  });

  // add flight and product entries to corresponding baseline categories (yearly values divided by 365)
  reportData.flight?.forEach((entry: any) => {
    emissions['baseline-mobility'] += (entry.causedEmissions || 0) / 365;
  });

  reportData.product?.forEach((entry: any) => {
    emissions['baseline-digital'] += (entry.causedEmissions || 0) / 365;
  });

  // add baseline constants to corresponding category (yearly values divided by 365)
  const constantData = reportData.constants || [];
  constantData.forEach((entry: any) => {
    emissions['baseline-constant'] += (entry.causedEmissions || 0) / 365;
  });

  return emissions;
}

export function calculateDailyEmissions(
  reportData: any,
  targetDate: Date
): Record<string, number> {
  const emissions: Record<string, number> = {
    'daily-activity': 0,
    'daily-mobility': 0,
    'daily-food': 0,
  };

  const targetDateString = targetDate.toDateString();
  const entryTypes = [
    { key: 'daily-mobility', data: reportData.mobility },
    { key: 'daily-food', data: reportData.food },
    { key: 'daily-activity', data: reportData.activity },
  ];

  // sum emissions for entries that occurred on target date (no calculation needed, use as is)
  entryTypes.forEach(({ key, data }) => {
    data?.forEach((entry: any) => {
      const entryDate = new Date(entry.occurredAt);
      if (entryDate.toDateString() === targetDateString) {
        emissions[key] += Math.abs(entry.causedEmissions || 0);
      }
    });
  });

  return emissions;
}

// Map backend entry types to frontend category keys
const ENTRY_TYPE_TO_CATEGORY_MAP: Record<string, string> = {
  baseline: 'baseline-constant',
  baselineMobility: 'baseline-mobility',
  baselineGeneralConsumption: 'baseline-general-consumption',
  baselineHousing: 'baseline-housing',
  activity: 'daily-activity',
  mobility: 'daily-mobility',
  food: 'daily-food',
  product: 'baseline-digital',
  flight: 'baseline-mobility',
};

export function processComparisonData(
  groupAverage: any,
  platformAverage: any,
  reportData?: any
): ComparisonData[] {
  const comparisonData: ComparisonData[] = [];

  // Calculate baseline constants once to use for all averages
  let baselineConstantEmissions = 0;
  if (reportData) {
    const baselineEmissions = calculateBaselineEmissions(reportData);
    baselineConstantEmissions = baselineEmissions['baseline-constant'] || 0;
  }

  // Process user averages if reportData is provided
  if (
    reportData &&
    reportData.userAverages &&
    Array.isArray(reportData.userAverages)
  ) {
    const userCategories: CategoryData[] = [];
    let userTotal = 0;

    reportData.userAverages.forEach((entry: any) => {
      const categoryKey = ENTRY_TYPE_TO_CATEGORY_MAP[entry.entryType];
      if (categoryKey && categoryKey !== 'baseline-constant') {
        // Skip baseline-constant from userAverages as we add it separately
        const category = ALL_CATEGORIES.find((c) => c.key === categoryKey);
        if (category) {
          const emissions =
            entry.entryType === 'baseline' || entry.entryType === 'flight'
              ? entry.value / 365
              : entry.value;

          userCategories.push({
            key: categoryKey,
            label: category.label,
            color: category.color,
            emissions: emissions,
          });
          userTotal += emissions;
        }
      }
    });

    // Add baseline constants for user averages (same value for all)
    if (baselineConstantEmissions > 0) {
      const category = ALL_CATEGORIES.find(
        (c) => c.key === 'baseline-constant'
      );
      if (category) {
        userCategories.push({
          key: 'baseline-constant',
          label: category.label,
          color: category.color,
          emissions: baselineConstantEmissions,
        });
        userTotal += baselineConstantEmissions;
      }
    }

    if (userCategories.length > 0) {
      comparisonData.push({
        label: 'Your Average',
        totalEmissions: userTotal,
        categories: userCategories,
      });
    }
  }

  // Process group average if available
  if (groupAverage && Array.isArray(groupAverage)) {
    const groupCategories: CategoryData[] = [];
    let groupTotal = 0;

    groupAverage.forEach((entry: any) => {
      const categoryKey = ENTRY_TYPE_TO_CATEGORY_MAP[entry.entryType];
      if (categoryKey && categoryKey !== 'baseline-constant') {
        // Skip baseline-constant from groupAverages as we add it separately
        const category = ALL_CATEGORIES.find((c) => c.key === categoryKey);
        if (category) {
          const emissions =
            entry.entryType === 'baseline' || entry.entryType === 'flight'
              ? entry.averageEmissions / 365
              : entry.averageEmissions;

          groupCategories.push({
            key: categoryKey,
            label: category.label,
            color: category.color,
            emissions: emissions,
          });
          groupTotal += emissions;
        }
      }
    });

    // Add baseline constants for group averages (same value for all)
    if (baselineConstantEmissions > 0) {
      const category = ALL_CATEGORIES.find(
        (c) => c.key === 'baseline-constant'
      );
      if (category) {
        groupCategories.push({
          key: 'baseline-constant',
          label: category.label,
          color: category.color,
          emissions: baselineConstantEmissions,
        });
        groupTotal += baselineConstantEmissions;
      }
    }

    if (groupCategories.length > 0) {
      comparisonData.push({
        label: 'Group Average',
        totalEmissions: groupTotal,
        categories: groupCategories,
      });
    }
  }

  // Process platform average if available
  if (platformAverage && Array.isArray(platformAverage)) {
    const platformCategories: CategoryData[] = [];
    let platformTotal = 0;

    platformAverage.forEach((entry: any) => {
      const categoryKey = ENTRY_TYPE_TO_CATEGORY_MAP[entry.entryType];
      if (categoryKey && categoryKey !== 'baseline-constant') {
        // Skip baseline-constant from platformAverages as we add it separately
        const category = ALL_CATEGORIES.find((c) => c.key === categoryKey);
        if (category) {
          const emissions =
            entry.entryType === 'baseline' || entry.entryType === 'flight'
              ? entry.averageEmissions / 365
              : entry.averageEmissions;

          platformCategories.push({
            key: categoryKey,
            label: category.label,
            color: category.color,
            emissions: emissions,
          });
          platformTotal += emissions;
        }
      }
    });

    // Add baseline constants for platform averages (same value for all)
    if (baselineConstantEmissions > 0) {
      const category = ALL_CATEGORIES.find(
        (c) => c.key === 'baseline-constant'
      );
      if (category) {
        platformCategories.push({
          key: 'baseline-constant',
          label: category.label,
          color: category.color,
          emissions: baselineConstantEmissions,
        });
        platformTotal += baselineConstantEmissions;
      }
    }

    if (platformCategories.length > 0) {
      comparisonData.push({
        label: 'Platform Average',
        totalEmissions: platformTotal,
        categories: platformCategories,
      });
    }
  }

  // Always add target value
  comparisonData.push({
    label: 'Target Value',
    totalEmissions: 1.5,
    categories: [
      {
        key: 'target',
        label: 'Target',
        color: 'var(--accent)',
        emissions: 1.5,
      },
    ],
  });

  return comparisonData;
}
